"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaClipboardList,
  FaGripVertical,
  FaLock,
  FaPlus,
  FaSave,
  FaTrash,
} from "react-icons/fa";
import Header from "../../../components/ManagementHeader";
import Footer from "../../../components/Footer";
import { supabase } from "../../../supabaseClient";

const SYSTEM_QUESTIONS = [
  { id: "system-roblox", label: "Roblox username", type: "short" },
  { id: "system-discord", label: "Discord username", type: "short" },
  { id: "system-email", label: "Email", type: "email" },
  { id: "system-roleplay", label: "Roleplay Name", type: "short" },
];

const QUESTION_TYPES = [
  { value: "short", label: "Short answer" },
  { value: "long", label: "Long answer" },
  { value: "select", label: "Multiple choice" },
  { value: "yes_no", label: "Yes / No" },
];

function makeQuestion(type = "long") {
  const id =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `q-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return {
    id,
    label: "",
    type,
    required: true,
    help_text: "",
    placeholder: "",
    options: type === "select" ? ["Option 1", "Option 2"] : [],
  };
}

function normaliseQuestions(value) {
  if (!Array.isArray(value)) return [];

  return value.map((question, index) => {
    if (typeof question === "string") {
      return {
        ...makeQuestion("long"),
        id: `legacy-${index}-${question.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 24)}`,
        label: question,
      };
    }

    return {
      ...makeQuestion(question?.type || "long"),
      ...question,
      id: question?.id || `legacy-${index}`,
      label: question?.label || question?.question || "",
      required: question?.required !== false,
      options: Array.isArray(question?.options) ? question.options : [],
    };
  });
}

function toDateTimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

export default function VacanciesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";

  const [vacancy, setVacancy] = useState({
    title: "",
    description: "",
    department: "",
    location: "",
    closing_date: "",
    application_intro: "",
    requirements: "",
    status: "Draft",
    is_active: false,
    questions: [],
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    if (!isNew) fetchVacancy();
  }, [id]);

  async function fetchVacancy() {
    setLoading(true);
    setError("");

    const { data, error: fetchError } = await supabase
      .from("dc_vacancies")
      .select("*")
      .eq("id", id)
      .single();

    if (fetchError) {
      setError(fetchError.message || "The vacancy could not be loaded.");
    } else if (data) {
      setVacancy({
        ...data,
        department: data.department || "",
        location: data.location || "",
        closing_date: toDateTimeLocal(data.closing_date),
        application_intro: data.application_intro || "",
        requirements: data.requirements || "",
        questions: normaliseQuestions(data.questions),
      });
    }

    setLoading(false);
  }

  function updateField(field, value) {
    setSaved(false);
    setVacancy((current) => ({ ...current, [field]: value }));
  }

  function addQuestion(type) {
    setSaved(false);
    setVacancy((current) => ({
      ...current,
      questions: [...current.questions, makeQuestion(type)],
    }));
  }

  function updateQuestion(index, patch) {
    setSaved(false);
    setVacancy((current) => ({
      ...current,
      questions: current.questions.map((question, questionIndex) =>
        questionIndex === index ? { ...question, ...patch } : question,
      ),
    }));
  }

  function removeQuestion(index) {
    setSaved(false);
    setVacancy((current) => ({
      ...current,
      questions: current.questions.filter((_, questionIndex) => questionIndex !== index),
    }));
  }

  function moveQuestion(fromIndex, toIndex) {
    if (fromIndex === null || fromIndex === toIndex || toIndex < 0) return;

    setSaved(false);
    setVacancy((current) => {
      const next = [...current.questions];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return { ...current, questions: next };
    });
  }

  async function saveVacancy(publish = false) {
    setError("");
    setSaved(false);

    if (!vacancy.title.trim()) {
      setError("Enter a vacancy title before saving.");
      return;
    }

    const invalidQuestion = vacancy.questions.find((question) => !question.label?.trim());
    if (invalidQuestion) {
      setError("Every custom question must have question text before the vacancy can be saved.");
      return;
    }

    const invalidChoice = vacancy.questions.find(
      (question) =>
        question.type === "select" &&
        (!Array.isArray(question.options) || question.options.filter((option) => option.trim()).length < 2),
    );
    if (invalidChoice) {
      setError("Multiple-choice questions need at least two options.");
      return;
    }

    setSaving(true);

    const {
      data: { user },
    } = await supabase.auth.getUser();

    const cleanedQuestions = vacancy.questions.map((question) => ({
      id: question.id,
      label: question.label.trim(),
      type: question.type,
      required: question.required !== false,
      help_text: question.help_text?.trim() || "",
      placeholder: question.placeholder?.trim() || "",
      options:
        question.type === "select"
          ? (question.options || []).map((option) => option.trim()).filter(Boolean)
          : [],
    }));

    const payload = {
      title: vacancy.title.trim(),
      description: vacancy.description?.trim() || null,
      department: vacancy.department?.trim() || null,
      location: vacancy.location?.trim() || null,
      closing_date: vacancy.closing_date ? new Date(vacancy.closing_date).toISOString() : null,
      application_intro: vacancy.application_intro?.trim() || null,
      requirements: vacancy.requirements?.trim() || null,
      questions: cleanedQuestions,
      question_schema_version: 2,
      status: publish ? "Open" : vacancy.status || "Draft",
      is_active: publish ? true : Boolean(vacancy.is_active),
      updated_by: user?.id || null,
    };

    let result;

    if (isNew) {
      result = await supabase
        .from("dc_vacancies")
        .insert({ ...payload, created_by: user?.id || null })
        .select("*")
        .single();
    } else {
      result = await supabase
        .from("dc_vacancies")
        .update(payload)
        .eq("id", id)
        .select("*")
        .single();
    }

    if (result.error) {
      setError(result.error.message || "The vacancy could not be saved.");
      setSaving(false);
      return;
    }

    setSaved(true);
    setSaving(false);

    if (isNew && result.data?.id) {
      navigate(`/intranet/management/vacancies/${result.data.id}`, { replace: true });
    } else if (result.data) {
      setVacancy((current) => ({
        ...current,
        ...result.data,
        closing_date: toDateTimeLocal(result.data.closing_date),
        questions: normaliseQuestions(result.data.questions),
      }));
    }
  }

  const totalQuestions = useMemo(() => SYSTEM_QUESTIONS.length + vacancy.questions.length, [vacancy.questions.length]);

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="pguh-vacancy-builder-loading">Loading vacancy builder…</main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="flex-1 pguh-vacancy-builder-page">
        <div className="nhsuk-width-container">
          <Link className="pguh-vacancy-back" to="/intranet/management/vacancies">
            <FaArrowLeft aria-hidden="true" /> Back to vacancies
          </Link>

          <section className="pguh-vacancy-builder-hero">
            <div>
              <span className="pguh-eyebrow">Application form builder</span>
              <h1>{isNew ? "Create a vacancy" : "Edit vacancy"}</h1>
              <p>
                Configure the vacancy, keep the four mandatory identity questions fixed, and drag custom questions into the order applicants should see them.
              </p>
            </div>
            <div className="pguh-vacancy-builder-hero__summary">
              <span>Application questions</span>
              <strong>{totalQuestions}</strong>
              <small>4 fixed · {vacancy.questions.length} custom</small>
              {!isNew && (
                <Link className="pguh-vacancy-builder-applications-link" to={`/intranet/management/vacancies/${id}/applications`}>
                  <FaClipboardList aria-hidden="true" /> View applications
                </Link>
              )}
            </div>
          </section>

          {error && (
            <div className="nhsuk-error-summary" role="alert">
              <h2 className="nhsuk-error-summary__title">There is a problem</h2>
              <div className="nhsuk-error-summary__body">{error}</div>
            </div>
          )}

          {saved && (
            <div className="pguh-vacancy-saved" role="status">
              <FaCheckCircle aria-hidden="true" /> Vacancy saved successfully.
            </div>
          )}

          <div className="pguh-vacancy-builder-layout">
            <div className="pguh-vacancy-builder-main">
              <section className="pguh-vacancy-builder-card">
                <div className="pguh-vacancy-builder-card__header">
                  <div>
                    <span className="pguh-eyebrow">Vacancy details</span>
                    <h2>Role information</h2>
                  </div>
                  <span className="pguh-vacancy-step">1</span>
                </div>

                <div className="pguh-vacancy-form-grid">
                  <label className="pguh-vacancy-field pguh-vacancy-field--full">
                    <span>Vacancy title</span>
                    <input
                      value={vacancy.title}
                      onChange={(event) => updateField("title", event.target.value)}
                      placeholder="e.g. Senior Staff Nurse - Band 6 - Emergency Department"
                    />
                  </label>

                  <label className="pguh-vacancy-field">
                    <span>Department</span>
                    <input
                      value={vacancy.department}
                      onChange={(event) => updateField("department", event.target.value)}
                      placeholder="Emergency Department"
                    />
                  </label>

                  <label className="pguh-vacancy-field">
                    <span>Location</span>
                    <select
                      value={vacancy.location}
                      onChange={(event) => updateField("location", event.target.value)}
                    >
                      <option value="">Select hospital</option>
                      <option value="Prince George University Hospital">
                        Prince George University Hospital
                      </option>
                      <option value="Duke of York Hospital">Duke of York Hospital</option>
                    </select>
                  </label>

                  <label className="pguh-vacancy-field pguh-vacancy-field--full">
                    <span>Vacancy summary</span>
                    <textarea
                      rows="4"
                      value={vacancy.description}
                      onChange={(event) => updateField("description", event.target.value)}
                      placeholder="Give staff a concise summary of the opportunity and who the role is suitable for."
                    />
                  </label>

                  <label className="pguh-vacancy-field pguh-vacancy-field--full">
                    <span>Application introduction</span>
                    <textarea
                      rows="3"
                      value={vacancy.application_intro}
                      onChange={(event) => updateField("application_intro", event.target.value)}
                      placeholder="Optional instructions shown immediately above the application form."
                    />
                  </label>

                  <label className="pguh-vacancy-field pguh-vacancy-field--full">
                    <span>Requirements / eligibility</span>
                    <textarea
                      rows="4"
                      value={vacancy.requirements}
                      onChange={(event) => updateField("requirements", event.target.value)}
                      placeholder="Optional eligibility, experience or role requirements."
                    />
                  </label>
                </div>
              </section>

              <section className="pguh-vacancy-builder-card">
                <div className="pguh-vacancy-builder-card__header">
                  <div>
                    <span className="pguh-eyebrow">Application form</span>
                    <h2>Questions</h2>
                  </div>
                  <span className="pguh-vacancy-step">2</span>
                </div>

                <div className="pguh-vacancy-system-heading">
                  <div>
                    <FaLock aria-hidden="true" />
                    <div>
                      <h3>Required applicant details</h3>
                      <p>These four questions are mandatory and cannot be changed, removed or reordered.</p>
                    </div>
                  </div>
                </div>

                <div className="pguh-vacancy-system-questions">
                  {SYSTEM_QUESTIONS.map((question, index) => (
                    <article key={question.id}>
                      <span className="pguh-vacancy-question-number">{index + 1}</span>
                      <div>
                        <strong>{question.label}</strong>
                        <small>Required · Fixed system question</small>
                      </div>
                      <FaLock aria-label="Locked" />
                    </article>
                  ))}
                </div>

                <div className="pguh-vacancy-custom-heading">
                  <div>
                    <h3>Custom questions</h3>
                    <p>Drag using the handle to change the order. Applicants see these after the four fixed questions.</p>
                  </div>
                  <div className="pguh-vacancy-add-menu">
                    <span>Add question:</span>
                    {QUESTION_TYPES.map((type) => (
                      <button key={type.value} type="button" onClick={() => addQuestion(type.value)}>
                        <FaPlus aria-hidden="true" /> {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                {vacancy.questions.length === 0 ? (
                  <div className="pguh-vacancy-no-custom">
                    <p>No custom questions yet. The application will contain only the four fixed applicant details.</p>
                  </div>
                ) : (
                  <div className="pguh-vacancy-question-list">
                    {vacancy.questions.map((question, index) => (
                      <article
                        key={question.id}
                        className={`pguh-vacancy-question ${dragIndex === index ? "is-dragging" : ""}`}
                        draggable
                        onDragStart={(event) => {
                          setDragIndex(index);
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", String(index));
                        }}
                        onDragOver={(event) => {
                          event.preventDefault();
                          event.dataTransfer.dropEffect = "move";
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          const from = Number(event.dataTransfer.getData("text/plain"));
                          moveQuestion(Number.isNaN(from) ? dragIndex : from, index);
                          setDragIndex(null);
                        }}
                        onDragEnd={() => setDragIndex(null)}
                      >
                        <button className="pguh-vacancy-drag-handle" type="button" aria-label={`Drag question ${index + 1}`}>
                          <FaGripVertical aria-hidden="true" />
                        </button>

                        <div className="pguh-vacancy-question-body">
                          <div className="pguh-vacancy-question-toolbar">
                            <span>Custom question {index + 1}</span>
                            <button type="button" onClick={() => removeQuestion(index)}>
                              <FaTrash aria-hidden="true" /> Remove
                            </button>
                          </div>

                          <div className="pguh-vacancy-question-grid">
                            <label className="pguh-vacancy-field pguh-vacancy-field--full">
                              <span>Question</span>
                              <input
                                value={question.label}
                                onChange={(event) => updateQuestion(index, { label: event.target.value })}
                                placeholder="Enter the question applicants should answer"
                              />
                            </label>

                            <label className="pguh-vacancy-field">
                              <span>Answer type</span>
                              <select
                                value={question.type}
                                onChange={(event) => {
                                  const nextType = event.target.value;
                                  updateQuestion(index, {
                                    type: nextType,
                                    options:
                                      nextType === "select" && question.options.length < 2
                                        ? ["Option 1", "Option 2"]
                                        : question.options,
                                  });
                                }}
                              >
                                {QUESTION_TYPES.map((type) => (
                                  <option key={type.value} value={type.value}>
                                    {type.label}
                                  </option>
                                ))}
                              </select>
                            </label>

                            <label className="pguh-vacancy-required-toggle">
                              <input
                                type="checkbox"
                                checked={question.required !== false}
                                onChange={(event) => updateQuestion(index, { required: event.target.checked })}
                              />
                              <span>Required question</span>
                            </label>

                            <label className="pguh-vacancy-field pguh-vacancy-field--full">
                              <span>Help text <small>(optional)</small></span>
                              <input
                                value={question.help_text || ""}
                                onChange={(event) => updateQuestion(index, { help_text: event.target.value })}
                                placeholder="Additional guidance shown below the question"
                              />
                            </label>

                            {(question.type === "short" || question.type === "long") && (
                              <label className="pguh-vacancy-field pguh-vacancy-field--full">
                                <span>Placeholder <small>(optional)</small></span>
                                <input
                                  value={question.placeholder || ""}
                                  onChange={(event) => updateQuestion(index, { placeholder: event.target.value })}
                                  placeholder="Example answer prompt"
                                />
                              </label>
                            )}

                            {question.type === "select" && (
                              <div className="pguh-vacancy-options pguh-vacancy-field--full">
                                <span>Answer options</span>
                                {(question.options || []).map((option, optionIndex) => (
                                  <div key={`${question.id}-option-${optionIndex}`}>
                                    <input
                                      value={option}
                                      onChange={(event) => {
                                        const options = [...question.options];
                                        options[optionIndex] = event.target.value;
                                        updateQuestion(index, { options });
                                      }}
                                      placeholder={`Option ${optionIndex + 1}`}
                                    />
                                    <button
                                      type="button"
                                      aria-label={`Remove option ${optionIndex + 1}`}
                                      onClick={() =>
                                        updateQuestion(index, {
                                          options: question.options.filter((_, currentIndex) => currentIndex !== optionIndex),
                                        })
                                      }
                                    >
                                      <FaTrash aria-hidden="true" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  className="pguh-vacancy-add-option"
                                  type="button"
                                  onClick={() =>
                                    updateQuestion(index, {
                                      options: [...(question.options || []), `Option ${(question.options || []).length + 1}`],
                                    })
                                  }
                                >
                                  <FaPlus aria-hidden="true" /> Add option
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            </div>

            <aside className="pguh-vacancy-builder-sidebar">
              <section className="pguh-vacancy-builder-card pguh-vacancy-publish-card">
                <span className="pguh-eyebrow">Publishing</span>
                <h2>Vacancy status</h2>

                <label className="pguh-vacancy-field">
                  <span>Status</span>
                  <select value={vacancy.status} onChange={(event) => updateField("status", event.target.value)}>
                    <option value="Draft">Draft</option>
                    <option value="Open">Open</option>
                    <option value="Closed">Closed</option>
                  </select>
                </label>

                <label className="pguh-vacancy-field">
                  <span>Closing date <small>(optional)</small></span>
                  <input
                    type="datetime-local"
                    value={vacancy.closing_date || ""}
                    onChange={(event) => updateField("closing_date", event.target.value)}
                  />
                </label>

                <label className="pguh-vacancy-visible-toggle">
                  <input
                    type="checkbox"
                    checked={Boolean(vacancy.is_active)}
                    onChange={(event) => updateField("is_active", event.target.checked)}
                  />
                  <span>
                    <strong>Visible to staff</strong>
                    <small>Only Open + visible vacancies appear on the staff vacancy page.</small>
                  </span>
                </label>

                <div className="pguh-vacancy-save-actions">
                  <button type="button" onClick={() => saveVacancy(false)} disabled={saving}>
                    <FaSave aria-hidden="true" /> {saving ? "Saving…" : "Save changes"}
                  </button>
                  <button
                    className="pguh-vacancy-publish-button"
                    type="button"
                    onClick={() => saveVacancy(true)}
                    disabled={saving}
                  >
                    <FaCheckCircle aria-hidden="true" /> Save & publish
                  </button>
                </div>
              </section>

              <section className="pguh-vacancy-builder-card pguh-vacancy-preview-card">
                <span className="pguh-eyebrow">Applicant preview</span>
                <h2>{vacancy.title || "Untitled vacancy"}</h2>
                <p>{vacancy.description || "Your vacancy summary will appear here."}</p>
                <dl>
                  <div><dt>Department</dt><dd>{vacancy.department || "Not set"}</dd></div>
                  <div><dt>Location</dt><dd>{vacancy.location || "Not set"}</dd></div>
                  <div><dt>Questions</dt><dd>{totalQuestions}</dd></div>
                </dl>
              </section>
            </aside>
          </div>
        </div>
      </main>

      <Footer />

      <style>{`
        .pguh-vacancy-builder-page { padding: 28px 0 64px; background: var(--nhs-light-grey); }
        .pguh-vacancy-builder-loading { display: grid; min-height: 60vh; place-items: center; font-weight: 700; }
        .pguh-vacancy-back { display: inline-flex; align-items: center; gap: 8px; margin-bottom: 16px; color: var(--nhs-blue); font-weight: 700; }
        .pguh-vacancy-builder-hero { display: flex; align-items: flex-end; justify-content: space-between; gap: 28px; margin-bottom: 22px; padding: 28px 32px; border-left: 8px solid var(--nhs-light-blue); color: #fff; background: linear-gradient(115deg, var(--nhs-dark-blue), var(--nhs-blue)); box-shadow: var(--pguh-shadow); }
        .pguh-vacancy-builder-hero h1, .pguh-vacancy-builder-hero p, .pguh-vacancy-builder-hero .pguh-eyebrow { color: #fff; }
        .pguh-vacancy-builder-hero h1 { margin: 0 0 8px; }
        .pguh-vacancy-builder-hero p { max-width: 820px; margin: 0; }
        .pguh-vacancy-builder-hero__summary { display: flex; min-width: 180px; flex-direction: column; padding: 14px 16px; border: 1px solid rgba(255,255,255,.35); background: rgba(0,48,135,.3); }
        .pguh-vacancy-builder-hero__summary span, .pguh-vacancy-builder-hero__summary small { color: rgba(255,255,255,.82); }
        .pguh-vacancy-builder-hero__summary strong { color: #fff; font-size: 1.7rem; }
        .pguh-vacancy-builder-applications-link { display:inline-flex; align-items:center; gap:7px; margin-top:11px; color:#fff; font-weight:700; }
        .pguh-vacancy-builder-applications-link:hover { color:#fff; text-decoration:underline; }
        .pguh-vacancy-saved { display: flex; align-items: center; gap: 10px; margin-bottom: 20px; padding: 12px 14px; border-left: 5px solid var(--nhs-green); background: #e6f4ea; font-weight: 700; }
        .pguh-vacancy-builder-layout { display: grid; grid-template-columns: minmax(0, 1fr) 330px; align-items: start; gap: 22px; }
        .pguh-vacancy-builder-main { display: grid; gap: 22px; }
        .pguh-vacancy-builder-sidebar { position: sticky; top: 18px; display: grid; gap: 18px; }
        .pguh-vacancy-builder-card { padding: 24px; border: 1px solid var(--pguh-border); background: #fff; box-shadow: var(--pguh-shadow); }
        .pguh-vacancy-builder-card__header { display: flex; align-items: center; justify-content: space-between; gap: 18px; margin-bottom: 22px; padding-bottom: 18px; border-bottom: 1px solid var(--pguh-border); }
        .pguh-vacancy-builder-card__header h2 { margin: 0; }
        .pguh-vacancy-step { display: grid; width: 36px; height: 36px; place-items: center; color: #fff; background: var(--nhs-blue); font-weight: 700; }
        .pguh-vacancy-form-grid, .pguh-vacancy-question-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
        .pguh-vacancy-field { display: flex; flex-direction: column; gap: 6px; font-weight: 700; }
        .pguh-vacancy-field--full { grid-column: 1 / -1; }
        .pguh-vacancy-field > span { color: var(--nhs-black); }
        .pguh-vacancy-field small { color: var(--nhs-dark-grey); font-weight: 400; }
        .pguh-vacancy-field input, .pguh-vacancy-field textarea, .pguh-vacancy-field select, .pguh-vacancy-options input { width: 100%; min-height: 44px; border: 2px solid var(--nhs-dark-grey); padding: 8px 10px; background: #fff; }
        .pguh-vacancy-field textarea { resize: vertical; }
        .pguh-vacancy-system-heading > div { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 14px; }
        .pguh-vacancy-system-heading svg { margin-top: 4px; color: var(--nhs-dark-blue); }
        .pguh-vacancy-system-heading h3, .pguh-vacancy-custom-heading h3 { margin: 0; }
        .pguh-vacancy-system-heading p, .pguh-vacancy-custom-heading p { margin: 3px 0 0; color: var(--nhs-dark-grey); }
        .pguh-vacancy-system-questions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-bottom: 28px; }
        .pguh-vacancy-system-questions article { display: grid; grid-template-columns: 34px minmax(0, 1fr) auto; align-items: center; gap: 10px; padding: 12px 14px; border: 1px solid var(--pguh-border); border-left: 5px solid var(--nhs-dark-blue); background: #f5f8fa; }
        .pguh-vacancy-system-questions article > div { display: flex; min-width: 0; flex-direction: column; }
        .pguh-vacancy-system-questions small { color: var(--nhs-dark-grey); }
        .pguh-vacancy-system-questions article > svg { color: var(--nhs-mid-grey); }
        .pguh-vacancy-question-number { display: grid; width: 30px; height: 30px; place-items: center; color: #fff; background: var(--nhs-dark-blue); font-size: .8rem; font-weight: 700; }
        .pguh-vacancy-custom-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 18px; margin-bottom: 14px; padding-top: 22px; border-top: 1px solid var(--pguh-border); }
        .pguh-vacancy-add-menu { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 7px; }
        .pguh-vacancy-add-menu > span { width: 100%; color: var(--nhs-dark-grey); font-size: .78rem; font-weight: 700; text-align: right; }
        .pguh-vacancy-add-menu button, .pguh-vacancy-add-option { display: inline-flex; min-height: 36px; align-items: center; gap: 6px; padding: 6px 9px; border: 1px solid var(--nhs-blue); color: var(--nhs-blue); background: #fff; font-size: .82rem; font-weight: 700; cursor: pointer; }
        .pguh-vacancy-add-menu button:hover, .pguh-vacancy-add-option:hover { color: #fff; background: var(--nhs-blue); }
        .pguh-vacancy-no-custom { padding: 24px; border: 2px dashed var(--pguh-border); color: var(--nhs-dark-grey); text-align: center; }
        .pguh-vacancy-no-custom p { margin: 0; }
        .pguh-vacancy-question-list { display: grid; gap: 12px; }
        .pguh-vacancy-question { display: grid; grid-template-columns: 46px minmax(0, 1fr); border: 1px solid var(--pguh-border); border-left: 5px solid var(--nhs-light-blue); background: #fff; transition: opacity .15s ease, transform .15s ease, box-shadow .15s ease; }
        .pguh-vacancy-question.is-dragging { opacity: .55; transform: scale(.995); box-shadow: var(--pguh-shadow-raised); }
        .pguh-vacancy-drag-handle { display: grid; min-height: 100%; place-items: start center; padding-top: 20px; border: 0; border-right: 1px solid var(--pguh-border); color: var(--nhs-dark-grey); background: var(--nhs-light-grey); cursor: grab; }
        .pguh-vacancy-drag-handle:active { cursor: grabbing; }
        .pguh-vacancy-question-body { min-width: 0; padding: 16px; }
        .pguh-vacancy-question-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 14px; }
        .pguh-vacancy-question-toolbar > span { color: var(--nhs-dark-grey); font-size: .78rem; font-weight: 700; text-transform: uppercase; }
        .pguh-vacancy-question-toolbar button { display: inline-flex; align-items: center; gap: 6px; border: 0; color: var(--nhs-red); background: transparent; font-weight: 700; text-decoration: underline; cursor: pointer; }
        .pguh-vacancy-required-toggle, .pguh-vacancy-visible-toggle { display: flex; align-items: center; gap: 9px; font-weight: 700; }
        .pguh-vacancy-required-toggle input, .pguh-vacancy-visible-toggle input { width: 22px; height: 22px; }
        .pguh-vacancy-options { display: grid; gap: 8px; }
        .pguh-vacancy-options > span { font-weight: 700; }
        .pguh-vacancy-options > div { display: grid; grid-template-columns: minmax(0, 1fr) 44px; gap: 6px; }
        .pguh-vacancy-options > div button { border: 1px solid var(--nhs-red); color: var(--nhs-red); background: #fff; cursor: pointer; }
        .pguh-vacancy-publish-card h2, .pguh-vacancy-preview-card h2 { margin-top: 0; }
        .pguh-vacancy-publish-card { display: grid; gap: 16px; }
        .pguh-vacancy-visible-toggle { align-items: flex-start; padding: 12px; background: var(--nhs-light-grey); }
        .pguh-vacancy-visible-toggle > span { display: flex; flex-direction: column; }
        .pguh-vacancy-visible-toggle small { margin-top: 3px; color: var(--nhs-dark-grey); font-weight: 400; }
        .pguh-vacancy-save-actions { display: grid; gap: 9px; padding-top: 4px; }
        .pguh-vacancy-save-actions button { display: inline-flex; min-height: 44px; align-items: center; justify-content: center; gap: 8px; border: 2px solid var(--nhs-blue); color: var(--nhs-blue); background: #fff; font-weight: 700; cursor: pointer; }
        .pguh-vacancy-save-actions .pguh-vacancy-publish-button { border-color: var(--nhs-green); border-bottom: 4px solid #00401e; color: #fff; background: var(--nhs-green); }
        .pguh-vacancy-save-actions button:disabled { opacity: .55; }
        .pguh-vacancy-preview-card p { color: var(--nhs-dark-grey); }
        .pguh-vacancy-preview-card dl { display: grid; gap: 8px; margin: 0; }
        .pguh-vacancy-preview-card dl > div { padding: 8px 10px; border-left: 4px solid var(--nhs-light-blue); background: var(--nhs-light-grey); }
        .pguh-vacancy-preview-card dt { color: var(--nhs-dark-grey); font-size: .75rem; font-weight: 700; text-transform: uppercase; }
        .pguh-vacancy-preview-card dd { margin: 2px 0 0; font-weight: 700; }
        @media (max-width: 1020px) {
          .pguh-vacancy-builder-layout { grid-template-columns: 1fr; }
          .pguh-vacancy-builder-sidebar { position: static; grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 760px) {
          .pguh-vacancy-builder-hero, .pguh-vacancy-custom-heading { align-items: flex-start; flex-direction: column; }
          .pguh-vacancy-builder-hero { padding: 24px 20px; }
          .pguh-vacancy-builder-hero__summary { width: 100%; }
          .pguh-vacancy-form-grid, .pguh-vacancy-question-grid, .pguh-vacancy-system-questions { grid-template-columns: 1fr; }
          .pguh-vacancy-field--full { grid-column: auto; }
          .pguh-vacancy-add-menu { justify-content: flex-start; }
          .pguh-vacancy-add-menu > span { text-align: left; }
          .pguh-vacancy-builder-sidebar { grid-template-columns: 1fr; }
        }
        @media (max-width: 500px) {
          .pguh-vacancy-builder-card { padding: 18px; }
          .pguh-vacancy-question { grid-template-columns: 38px minmax(0, 1fr); }
          .pguh-vacancy-question-toolbar { align-items: flex-start; flex-direction: column; }
        }
      `}</style>
    </div>
  );
}
